import React from "react";
import { Trans, useTranslation } from "react-i18next";
import { makeStyles } from "tss-react/mui";

import { Box, Card, CardContent, Link, Theme, Typography } from "@mui/material";
import { PrimaryButton, SecondaryButton } from "@shared/CustomButton";
import emteriaTeamImage from "@shared/images/emteriaTeam.webp";

const useStyles = makeStyles()((theme: Theme) => ({
    container: {
        maxWidth: 900,
        width: "100%",
        margin: "0 auto",
        borderRadius: theme.spacing(2),
    },
    content: {
        padding: theme.spacing(4),
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(3),
    },
    mainContent: {
        display: "flex",
        flexDirection: "row",
        gap: theme.spacing(4),
        alignItems: "flex-start",
        marginTop: theme.spacing(2),
        [theme.breakpoints.down("md")]: {
            flexDirection: "column",
        },
    },
    imageContainer: {
        flex: "0 0 auto",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 300,
        maxWidth: 420,
        [theme.breakpoints.down("md")]: {
            minWidth: "auto",
            maxWidth: "100%",
            width: "100%",
        },
    },
    image: {
        maxWidth: "100%",
        height: "auto",
        objectFit: "contain",
        maxHeight: 420,
    },
    textContainer: {
        flex: 1,
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(2),
    },
    title: {
        fontWeight: 600,
        marginBottom: theme.spacing(2),
        color: theme.palette.emteria.text.primary,
        textAlign: "left",
    },
    question: {
        color: theme.palette.emteria.text.primary,
        marginBottom: theme.spacing(1),
    },
    description: {
        color: theme.palette.emteria.text.secondary,
        marginBottom: theme.spacing(1),
    },
    contactText: {
        color: theme.palette.emteria.text.secondary,
        marginBottom: theme.spacing(1),
    },
    emailLink: {
        color: theme.palette.primary.main,
        textDecoration: "none",
        "&:hover": {
            textDecoration: "underline",
        },
    },
    actions: {
        padding: theme.spacing(3, 4),
        display: "flex",
        gap: theme.spacing(2),
        justifyContent: "space-between",
    },
    actionsLeft: {
        display: "flex",
        gap: theme.spacing(2),
    },
}));

interface GetInTouchStepProps {
    onBack: () => void;
    onContact: () => void;
}

export const GetInTouchStep: React.FC<GetInTouchStepProps> = ({ onBack, onContact }) => {
    const { classes } = useStyles();
    const { t } = useTranslation(["modals", "common"]);

    return (
        <Card className={classes.container} elevation={3}>
            <CardContent className={classes.content}>
                <Typography variant="h6" className={classes.title}>
                    {t("modals:enrollmentDialog.buildCustomStep.title")}
                </Typography>
                <Box className={classes.mainContent}>
                    <Box className={classes.imageContainer}>
                        <img src={emteriaTeamImage} alt="Emteria team" className={classes.image} />
                    </Box>
                    <Box className={classes.textContainer}>
                        <Typography variant="body1" className={classes.question}>
                            {t("modals:enrollmentDialog.buildCustomStep.question")}
                        </Typography>
                        <Typography variant="body1" className={classes.description}>
                            {t("modals:enrollmentDialog.buildCustomStep.description")}
                        </Typography>
                        <Typography variant="body1" className={classes.contactText}>
                            <Trans
                                ns="modals"
                                i18nKey="enrollmentDialog.buildCustomStep.contactText"
                                components={{
                                    email: (
                                        <Link href="mailto:sales@emteria.com" className={classes.emailLink}>
                                            sales@emteria.com
                                        </Link>
                                    ),
                                }}
                            />
                        </Typography>
                    </Box>
                </Box>
            </CardContent>
            <Box className={classes.actions}>
                <Box className={classes.actionsLeft}>
                    <SecondaryButton onClick={onBack}>{t("common:button.goBack")}</SecondaryButton>
                </Box>
                <PrimaryButton onClick={onContact}>{t("modals:enrollmentDialog.buildCustomStep.buttons.contact")}</PrimaryButton>
            </Box>
        </Card>
    );
};
